import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, Mail, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';

// Admins created straight from Admin → Users get a password but no verified
// email, and the platform blocks sign-in until the address is confirmed. The
// one-time code is emailed, but the only screen that accepted it lived inside
// the customer sign-up form — unreachable once the account already exists. This
// panel is the missing half: it re-sends the code and takes it back in.

const inputCls = 'bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60';

function readableError(err, fallback) {
  const msg = err?.message;
  if (msg && msg !== '[object Object]') return msg;
  const detail = err?.data?.detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

export default function AdminEmailVerification({ defaultEmail = '', onDone, onCancel }) {
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendCode = async (e) => {
    e.preventDefault();
    setError('');
    const clean = email.trim().toLowerCase();
    if (!clean) { setError('Enter the email address on the account.'); return; }
    setLoading(true);
    try {
      await base44.auth.resendOtp(clean);
      setStep('code');
    } catch (err) {
      setError(readableError(err, 'Could not send a code to that address.'));
    }
    setLoading(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) { setError('Enter the verification code from your email.'); return; }
    setLoading(true);
    try {
      await base44.auth.verifyOtp({ email: email.trim().toLowerCase(), otpCode: code.trim() });
      onDone?.();
    } catch (err) {
      setError(readableError(err, 'That code was not accepted. Please try again.'));
    }
    setLoading(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex justify-center mb-8">
        <div className="w-16 h-16 bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-8 h-8 text-primary" />
        </div>
      </div>

      <p className="text-primary text-xs font-bold tracking-widest mb-2 text-center">VERIFY YOUR EMAIL</p>
      <h1 className="text-white font-black text-3xl uppercase text-center mb-3">ACTIVATE ACCOUNT</h1>
      <p className="text-white/30 text-sm text-center mb-8">
        {step === 'email'
          ? 'We’ll send a verification code to the address on the account.'
          : <>Enter the code we sent to <span className="text-white">{email}</span>.</>}
      </p>

      {step === 'email' ? (
        <form onSubmit={sendCode} className="space-y-5">
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">EMAIL ADDRESS</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${inputCls} pl-9`}
                placeholder="admin@email.com"
              />
            </div>
          </div>

          {error && <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {loading ? 'SENDING...' : 'SEND VERIFICATION CODE'}
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-5">
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">VERIFICATION CODE</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${inputCls} text-center text-xl tracking-widest`}
              placeholder="Enter code"
              maxLength={8}
            />
          </div>

          {error && <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {loading ? 'VERIFYING...' : 'VERIFY & ACTIVATE'}
          </button>

          <button
            type="button"
            onClick={() => { setError(''); setStep('email'); }}
            className="w-full text-white/30 hover:text-white text-xs font-bold tracking-wider transition-colors"
          >
            USE A DIFFERENT EMAIL
          </button>
        </form>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="w-full mt-6 flex items-center justify-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-wider transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SIGN IN
      </button>
    </motion.div>
  );
}